import { initSiteHeaderMenu } from "./header-menu.js";
import { initMindRelatedStack } from "./mind-related.js";
import { initTopPage } from "./top.js";

function initEnvOnboarding() {
  const root = document.querySelector(".js-envOnboarding");
  if (!root) return;

  const tabs = [...root.querySelectorAll(".js-envOnboardingTab")];
  const yearItems = [...root.querySelectorAll(".envOnboarding__year")];
  const mq = window.matchMedia("(min-width: 769px)");

  const getPanel = (yearEl) => yearEl.querySelector("[data-env-panel]");
  const getTab = (yearEl) => yearEl.querySelector(".js-envOnboardingTab");

  const clearInlineHeight = (panel) => {
    panel.style.height = "";
  };

  const openSp = (panel) => {
    panel.hidden = false;
    panel.style.height = `${panel.scrollHeight}px`;

    const onEnd = (event) => {
      if (event.propertyName !== "height") return;
      panel.style.height = "auto";
      panel.removeEventListener("transitionend", onEnd);
    };
    panel.addEventListener("transitionend", onEnd);
  };

  const closeSp = (panel) => {
    if (panel.hidden) {
      panel.hidden = false;
      panel.style.height = "0px";
      return;
    }

    const current = panel.style.height;
    if (current === "auto" || current === "") {
      panel.style.height = `${panel.scrollHeight}px`;
    }

    requestAnimationFrame(() => {
      panel.style.height = "0px";
    });
  };

  // 開いた年次バーをヘッダー直下へ寄せ、画面内に残す
  const scrollTriggerToTop = (trigger, panels) => {
    if (!trigger) return;

    const getOffset = () => {
      const header = document.querySelector(".siteHeader");
      return (header?.getBoundingClientRect().height || 0) + 8;
    };

    const scrollToTrigger = (behavior) => {
      const top = window.scrollY + trigger.getBoundingClientRect().top - getOffset();
      window.scrollTo({ top: Math.max(0, top), behavior });
    };

    scrollToTrigger("smooth");

    if (!panels.length) return;

    let pending = panels.length;
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      // 高さアニメ後にレイアウトが変わっても上端へ揃える
      scrollToTrigger("auto");
    };

    const onEnd = (event) => {
      if (event.propertyName !== "height") return;
      event.currentTarget.removeEventListener("transitionend", onEnd);
      pending -= 1;
      if (pending <= 0) finish();
    };

    panels.forEach((panel) => {
      panel.addEventListener("transitionend", onEnd);
    });
    window.setTimeout(finish, 450);
  };

  const activatePc = (year) => {
    const yearStr = String(year);

    yearItems.forEach((yearEl) => {
      const tab = getTab(yearEl);
      const panel = getPanel(yearEl);
      if (!tab || !panel) return;

      const on = tab.dataset.year === yearStr;
      clearInlineHeight(panel);
      tab.setAttribute("aria-selected", on ? "true" : "false");
      tab.setAttribute("aria-expanded", on ? "true" : "false");
      tab.tabIndex = on ? 0 : -1;
      yearEl.classList.toggle("is-current", on);
      panel.hidden = !on;
    });
  };

  const activateSp = (year, { toggle = true } = {}) => {
    const yearStr = String(year);
    const current = root.querySelector(".envOnboarding__year.is-current");
    const currentTab = current ? getTab(current) : null;
    const nextTab = tabs.find((tab) => tab.dataset.year === yearStr);

    // 同じ年をもう一度 → 閉じる
    if (toggle && currentTab?.dataset.year === yearStr) {
      closeSp(getPanel(current));
      current.classList.remove("is-current");
      currentTab.setAttribute("aria-selected", "false");
      currentTab.setAttribute("aria-expanded", "false");
      currentTab.tabIndex = 0;
      return;
    }

    const animatingPanels = [];

    yearItems.forEach((yearEl) => {
      const tab = getTab(yearEl);
      const panel = getPanel(yearEl);
      if (!tab || !panel) return;

      const on = tab.dataset.year === yearStr;
      const wasOpen = yearEl.classList.contains("is-current");

      if (on) {
        panel.hidden = false;
        yearEl.classList.add("is-current");
        tab.setAttribute("aria-selected", "true");
        tab.setAttribute("aria-expanded", "true");
        tab.tabIndex = 0;
        if (!wasOpen) {
          openSp(panel);
          animatingPanels.push(panel);
        }
      } else {
        if (wasOpen) {
          closeSp(panel);
          animatingPanels.push(panel);
        }
        yearEl.classList.remove("is-current");
        tab.setAttribute("aria-selected", "false");
        tab.setAttribute("aria-expanded", "false");
        tab.tabIndex = -1;
      }
    });

    // 開いた年次バーを画面上部へ
    if (nextTab && animatingPanels.length) {
      scrollTriggerToTop(nextTab, animatingPanels);
    }
  };

  const syncMode = () => {
    const currentTab =
      root.querySelector(".envOnboarding__year.is-current .js-envOnboardingTab") ||
      tabs[0];
    const year = currentTab?.dataset.year || tabs[0]?.dataset.year;
    if (!year) return;

    if (mq.matches) {
      activatePc(year);
      return;
    }

    // SP: hidden を外して高さで開閉
    yearItems.forEach((yearEl) => {
      const panel = getPanel(yearEl);
      const tab = getTab(yearEl);
      if (!panel || !tab) return;

      panel.hidden = false;
      const on = yearEl.classList.contains("is-current");
      panel.style.height = on ? "auto" : "0px";
      tab.setAttribute("aria-expanded", on ? "true" : "false");
    });

    // どれも開いていなければ初期は閉じたまま（トグル後の resize 考慮）
    // 初回で is-current が付いている場合は上で auto 済み
  };

  tabs.forEach((tab) => {
    tab.addEventListener("click", () => {
      if (mq.matches) {
        activatePc(tab.dataset.year);
        return;
      }
      activateSp(tab.dataset.year, { toggle: true });
    });

    tab.addEventListener("keydown", (event) => {
      const index = tabs.indexOf(tab);
      if (index < 0) return;

      let next = -1;
      if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        next = (index + 1) % tabs.length;
      } else if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        next = (index - 1 + tabs.length) % tabs.length;
      } else if (event.key === "Home") {
        next = 0;
      } else if (event.key === "End") {
        next = tabs.length - 1;
      } else {
        return;
      }

      event.preventDefault();
      const target = tabs[next];
      if (mq.matches) {
        activatePc(target.dataset.year);
      } else {
        activateSp(target.dataset.year, { toggle: false });
      }
      target.focus();
    });
  });

  syncMode();
  mq.addEventListener("change", syncMode);
}

function initEnvProgramsModal() {
  const modal = document.querySelector(".js-envProgramsModal");
  if (!modal) return;

  const bodies = modal.querySelectorAll("[data-modal-content]");
  const openButtons = document.querySelectorAll(".js-envProgramsModalOpen");
  const closeButtons = modal.querySelectorAll(".js-envProgramsModalClose");
  let lastFocus = null;

  const setOpen = (open, id) => {
    if (open && id) {
      let found = false;
      bodies.forEach((body) => {
        const match = body.dataset.modalContent === id;
        body.hidden = !match;
        if (match) found = true;
      });
      if (!found) return;

      lastFocus = document.activeElement;
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      document.body.classList.add("is-envProgramsModalOpen");
      modal.querySelector(".js-envProgramsModalCloseBtn")?.focus();
      return;
    }

    modal.hidden = true;
    modal.setAttribute("aria-hidden", "true");
    document.body.classList.remove("is-envProgramsModalOpen");
    bodies.forEach((body) => {
      body.hidden = true;
    });
    if (lastFocus && typeof lastFocus.focus === "function") {
      lastFocus.focus();
    }
    lastFocus = null;
  };

  openButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      setOpen(true, btn.dataset.modal);
    });
  });

  closeButtons.forEach((btn) => {
    btn.addEventListener("click", () => setOpen(false));
  });

  document.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !modal.hidden) {
      setOpen(false);
    }
  });
}

function initScrollFadeIn() {
  if (document.body.classList.contains("page-top")) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const main = document.querySelector("main.site-main");
  if (!main) return;

  // main 配下の section（ネスト含む）。MV・まとまり親は除外し、子単位でフェードする
  const targets = [
    ...main.querySelectorAll("section:not(.lowerMv):not(.worksRoles)"),
    ...main.querySelectorAll(".worksRole"),
  ];
  if (targets.length === 0) return;

  targets.forEach((el) => {
    el.classList.add("js-scrollFade");
  });

  // threshold を高くすると、mindList など高さのある section が
  // 実機 SP で一度も交差せず opacity:0 のまま残る（viewport < 要素高×閾値）
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add("is-inview");
        observer.unobserve(entry.target);
      });
    },
    {
      root: null,
      rootMargin: "0px 0px -15% 0px",
      threshold: 0,
    }
  );

  targets.forEach((el) => {
    observer.observe(el);
  });
}

document.addEventListener("DOMContentLoaded", () => {
  initSiteHeaderMenu();
  initEnvOnboarding();
  initEnvProgramsModal();
  initMindRelatedStack();
  initScrollFadeIn();
  if (document.querySelector(".topPage")) {
    initTopPage();
  }
});

document.querySelectorAll(".recruitFaq__question").forEach((button) => {
  button.addEventListener("click", () => {
    const item = button.closest(".recruitFaq__item");
    const answer = item.querySelector(".recruitFaq__answer");

    if (item.classList.contains("is-open")) {
      answer.style.height = `${answer.scrollHeight}px`;

      requestAnimationFrame(() => {
        answer.style.height = "0";
      });

      item.classList.remove("is-open");
    } else {
      answer.style.height = `${answer.scrollHeight}px`;
      item.classList.add("is-open");

      answer.addEventListener(
        "transitionend",
        function handler() {
          answer.style.height = "auto";
          answer.removeEventListener("transitionend", handler);
        },
        { once: true }
      );
    }
  });
});
